import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import GridBackground from './GridBackground';

export const CompleteProfile = () => {
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        organization: '',
        role: '',
        customRole: ''
    });

    const [showCustomRole, setShowCustomRole] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/');
            return;
        }

        // Check profile completion from context
        if (userProfile?.profileCompleted) {
            navigate('/search');
        }
    }, [user, userProfile, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = await user.getIdToken();
            const response = await fetch(`${API_URL}/users/${user.uid}/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    profileCompleted: true
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update profile');
            }

            navigate('/search');
        } catch (error) {
            console.error('Error updating profile:', error);
        }
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleOptionSelect = (field, value) => {
        if (value === 'Other') {
            setShowCustomRole(true);
            setFormData(prev => ({
                ...prev,
                role: prev.customRole
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value,
                customRole: prev.role
            }));
            setShowCustomRole(false);
        }
    };

    const roleOptions = [
        'Software Engineer',
        'Data Scientist',
        'Product Manager',
        'Researcher',
        'Student',
        'Other'
    ];

    return (
        <div className="relative w-full h-screen bg-black overflow-hidden">
            <GridBackground />
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full max-w-md p-8 bg-black/50 border border-purple-900/30 rounded-lg backdrop-blur-sm">
                    <h2 className="text-2xl font-bold text-white mb-6">Complete Your Profile</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                First Name
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg 
                                    text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Last Name
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg 
                                    text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Organization
                            </label>
                            <input
                                type="text"
                                name="organization"
                                value={formData.organization}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg 
                                    text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Occupation
                            </label>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                {roleOptions.map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => handleOptionSelect('role', option)}
                                        className={`px-4 py-2 rounded-lg border ${formData.role === option
                                            ? 'bg-purple-600 border-purple-500 text-white'
                                            : 'bg-black/50 border-purple-900/30 text-gray-300 hover:border-purple-500/50'
                                            }`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                            {showCustomRole && (
                                <input
                                    type="text"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    placeholder="Enter your occupation"
                                    required
                                    className="w-full px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg 
                                        text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                                />
                            )}
                        </div>

                        <button
                            type="submit"
                            className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 
                                hover:from-purple-700 hover:to-blue-700 text-white rounded-lg 
                                transition-colors duration-200"
                        >
                            Complete Profile
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}; 